/* Components clean up on theme-editor reload and honor reduced motion. */
class NVCarousel extends HTMLElement {
  connectedCallback() {
    this.abort?.abort(); this.abort = new AbortController();
    const { signal } = this.abort;
    this.track = this.querySelector('[data-nv-track]');
    if (!this.track) return;
    this.prev = this.querySelector('[data-nv-prev]'); this.next = this.querySelector('[data-nv-next]');
    this.dots = this.querySelector('[data-nv-dots]'); this.positions = [0]; this.index = 0;
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.prev?.addEventListener('click', () => this.move(-1), { signal });
    this.next?.addEventListener('click', () => this.move(1), { signal });
    this.track.addEventListener('scroll', () => {
      cancelAnimationFrame(this.frame); this.frame = requestAnimationFrame(() => this.sync());
    }, { signal, passive: true });
    this.addEventListener('keydown', e => {
      if (!e.target.matches('[data-nv-track], [data-nv-prev], [data-nv-next], [data-nv-dots] button')) return;
      if (['ArrowLeft', 'ArrowRight'].includes(e.key)) { e.preventDefault(); this.move(e.key === 'ArrowLeft' ? -1 : 1); }
    }, { signal });
    this.track.tabIndex = 0;
    this.track.setAttribute('role', 'region');
    this.track.setAttribute('aria-label', this.closest('.nv-module')?.querySelector('h2')?.textContent || this.dataset.label || 'Slideshow');
    this.addEventListener('pointerenter', () => { this.hovered = true; this.stop(); }, { signal });
    this.addEventListener('pointerleave', () => { this.hovered = false; this.start(); }, { signal });
    this.addEventListener('focusin', () => this.stop(), { signal });
    this.addEventListener('focusout', () => queueMicrotask(() => this.start()), { signal });
    document.addEventListener('visibilitychange', () => this.start(), { signal });
    this.motion.addEventListener('change', () => this.start(), { signal });
    this.pauseButton = this.querySelector('[data-nv-pause]');
    this.pauseButton?.addEventListener('click', () => {
      this.paused = !this.paused; this.pauseButton.setAttribute('aria-pressed', String(this.paused));
      this.pauseButton.textContent = this.paused ? this.pauseButton.dataset.playLabel : this.pauseButton.dataset.pauseLabel; this.start();
    }, { signal });
    document.addEventListener('shopify:block:select', e => {
      const slide = e.target.closest('.nv-slide,.nv-hero__slide');
      if (!slide || !this.contains(slide)) return;
      this.track.scrollTo({ left: slide.offsetLeft - this.track.firstElementChild.offsetLeft, behavior: 'instant' });
      this.paused = true; this.stop();
    }, { signal });
    this.observer = new ResizeObserver(() => this.measure()); this.observer.observe(this.track);
    this.intersection = new IntersectionObserver(([entry]) => { this.inView = entry.isIntersecting; this.start(); });
    this.intersection.observe(this); this.measure();
  }
  measure() {
    const children = [...this.track.children];
    if (!children.length || this.track.clientWidth < 1) { this.stop(); return; }
    const max = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    const positions = [0];
    for (const child of children.slice(1)) {
      const p = Math.min(max, Math.max(0, child.offsetLeft - children[0].offsetLeft));
      if (p > positions[positions.length - 1] + 2) positions.push(p);
    }
    if (max > positions[positions.length - 1] + 2) positions.push(max);
    const countChanged = positions.length !== this.positions.length;
    this.positions = positions; this.dataset.single = String(positions.length < 2);
    if (this.dots && (countChanged || !this.dots.children.length)) {
      this.dots.replaceChildren(...positions.map((_, i) => {
        const b = document.createElement('button'); b.type = 'button';
        b.setAttribute('aria-label', `${i + 1} / ${positions.length}`);
        b.addEventListener('click', () => this.go(i)); return b;
      }));
    }
    this.sync(); this.start();
  }
  sync() {
    const x = Math.abs(this.track.scrollLeft);
    this.index = this.positions.reduce((best, p, i) => Math.abs(p - x) < Math.abs(this.positions[best] - x) ? i : best, 0);
    if (this.prev) this.prev.disabled = this.index === 0;
    if (this.next) this.next.disabled = this.index === this.positions.length - 1;
    this.dots?.querySelectorAll('button').forEach((b, i) => b.setAttribute('aria-current', String(i === this.index)));
  }
  go(i) {
    const direction = getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
    this.track.scrollTo({ left: direction * this.positions[Math.max(0, Math.min(i, this.positions.length - 1))], behavior: this.motion.matches ? 'instant' : 'smooth' });
  }
  move(delta) { this.go(this.index + delta); }
  start() {
    this.stop();
    if (this.dataset.autoplay !== 'true' || this.positions.length < 2 || !this.inView || this.hovered || this.paused || this.motion.matches || document.hidden || this.matches(':focus-within')) return;
    this.timer = setInterval(() => this.go((this.index + 1) % this.positions.length), Number(this.dataset.interval) || 6000);
  }
  stop() { clearInterval(this.timer); }
  disconnectedCallback() { this.stop(); this.abort?.abort(); this.observer?.disconnect(); this.intersection?.disconnect(); cancelAnimationFrame(this.frame); }
}
class NVVideo extends HTMLElement {
  connectedCallback() {
    this.abort?.abort(); this.abort = new AbortController();
    this.querySelector('button')?.addEventListener('click', () => {
      const button = this.querySelector('button'); if (!button) return;
      const id = button.dataset.nvVideo, provider = button.dataset.provider;
      if ((provider === 'youtube' && !/^[\w-]{6,20}$/.test(id)) || (provider === 'vimeo' && !/^\d+$/.test(id))) return;
      if (!['youtube', 'vimeo'].includes(provider)) return;
      const frame = document.createElement('iframe');
      frame.src = provider === 'youtube' ? `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0` : `https://player.vimeo.com/video/${id}?autoplay=1`;
      frame.title = button.getAttribute('aria-label'); frame.allow = 'autoplay; encrypted-media; picture-in-picture'; frame.allowFullscreen = true;
      this.replaceChildren(frame); frame.focus();
    }, { signal: this.abort.signal });
  }
  disconnectedCallback() { this.abort?.abort(); }
}
class NVBackgroundVideo extends HTMLElement {
  connectedCallback() {
    this.abort?.abort(); this.abort = new AbortController(); const { signal } = this.abort;
    this.motion = matchMedia('(prefers-reduced-motion: reduce)'); this.phone = matchMedia('(max-width:749px)');
    this.videos = [...this.querySelectorAll('video')]; this.toggle = this.querySelector('[data-nv-video-toggle]');
    this.paused = this.motion.matches || this.dataset.autoplay !== 'true';
    this.toggle?.addEventListener('click', () => { this.paused = !this.paused; this.manualPlayback = !this.paused; this.sync(); }, { signal });
    this.motion.addEventListener('change', () => this.sync(), { signal });
    this.phone.addEventListener('change', () => this.sync(), { signal });
    document.addEventListener('visibilitychange', () => this.sync(), { signal });
    this.observer = new IntersectionObserver(([e]) => { this.visible = e.isIntersecting; this.sync(); }); this.observer.observe(this);
  }
  sync() {
    const mobile = this.querySelector('.nv-banner-video--mobile');
    const current = this.phone.matches && mobile ? mobile : this.videos[0];
    this.videos.forEach(v => {
      if (v !== current || document.hidden || !this.visible || this.paused || (!this.manualPlayback && (this.motion.matches || this.dataset.autoplay !== 'true'))) v.pause();
      else v.play().catch(() => {});
    });
    if (this.toggle) {
      const stopped = this.paused || (!this.manualPlayback && this.motion.matches);
      this.toggle.setAttribute('aria-pressed', String(!!stopped)); this.toggle.textContent = stopped ? '▶' : 'Ⅱ';
      this.toggle.setAttribute('aria-label', stopped ? this.toggle.dataset.playLabel : this.toggle.dataset.pauseLabel);
    }
  }
  disconnectedCallback() { this.abort?.abort(); this.observer?.disconnect(); this.videos?.forEach(v => v.pause()); }
}
class NVTabs extends HTMLElement {
  connectedCallback() {
    this.abort?.abort(); this.abort = new AbortController(); const { signal } = this.abort;
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.select(index), { signal });
      tab.addEventListener('keydown', e => {
        let next = index;
        if (e.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
        else if (e.key === 'ArrowLeft') next = (index + this.tabs.length - 1) % this.tabs.length;
        else if (e.key === 'Home') next = 0;
        else if (e.key === 'End') next = this.tabs.length - 1;
        else return;
        e.preventDefault(); this.select(next); this.tabs[next].focus();
      }, { signal });
    });
  }
  select(index) {
    this.tabs.forEach((tab, i) => {
      tab.setAttribute('aria-selected', String(i === index)); tab.tabIndex = i === index ? 0 : -1;
      const panel = this.querySelector(`[id="${CSS.escape(tab.getAttribute('aria-controls'))}"]`);
      if (panel) panel.hidden = i !== index;
    });
  }
  disconnectedCallback() { this.abort?.abort(); }
}
class NVStickyCart extends HTMLElement {
  connectedCallback() {
    this.abort?.abort(); this.abort = new AbortController(); const { signal } = this.abort;
    this.main = document.querySelector('product-info'); this.button = this.querySelector('[data-nv-sticky-buy]');
    this.original = this.main?.querySelector('product-form button[name="add"]');
    if (!this.original || !this.button) { this.hidden = true; return; }
    const visibility = () => { cancelAnimationFrame(this.frame); this.frame = requestAnimationFrame(() => { this.hidden = this.original.getBoundingClientRect().bottom >= 0; }); };
    window.addEventListener('scroll', visibility, { signal, passive: true });
    window.addEventListener('resize', visibility, { signal });
    this.mutation = new MutationObserver(() => this.sync()); this.mutation.observe(this.main, { subtree: true, childList: true, attributes: true, attributeFilter: ['disabled', 'aria-disabled'] });
    this.button.addEventListener('click', () => {
      const form = this.main.querySelector('form[data-type="add-to-cart-form"]'); const buy = form?.querySelector('[name="add"]');
      if (buy && !buy.disabled && buy.getAttribute('aria-disabled') !== 'true') { document.querySelector('cart-drawer')?.setActiveElement?.(this.button); form.requestSubmit(buy); }
    }, { signal });
    this.sync(); visibility();
  }
  sync() {
    const buy = this.main.querySelector('product-form button[name="add"]'); const price = this.main.querySelector('[id^="price-"]');
    this.button.disabled = !buy || buy.disabled || buy.getAttribute('aria-disabled') === 'true';
    if (buy) this.button.textContent = buy.querySelector('span')?.textContent.trim() || buy.textContent.trim();
    const target = this.querySelector('[data-nv-sticky-price]');
    if (price && target) target.textContent = price.querySelector(price.querySelector('.price--on-sale') ? '.price-item--sale' : '.price__regular .price-item--regular')?.textContent.trim() || '';
  }
  disconnectedCallback() { this.abort?.abort(); this.mutation?.disconnect(); cancelAnimationFrame(this.frame); }
}
class NVSupport extends HTMLElement {
  connectedCallback() {
    this.abort?.abort(); this.abort = new AbortController(); const { signal } = this.abort;
    const top = this.querySelector('[data-nv-top]'), dialog = this.querySelector('dialog'), trigger = this.querySelector('[data-nv-support-open]');
    const sync = () => { if (top) top.hidden = scrollY < Number(this.dataset.threshold || 600); };
    window.addEventListener('scroll', sync, { signal, passive: true }); sync();
    top?.addEventListener('click', () => window.scrollTo({ top: 0, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' }), { signal });
    trigger?.addEventListener('click', () => dialog?.showModal(), { signal });
    dialog?.querySelector('[data-nv-close]')?.addEventListener('click', () => dialog.close(), { signal });
    dialog?.addEventListener('click', e => { if (e.target === dialog) { const r = dialog.getBoundingClientRect(); if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) dialog.close(); } }, { signal });
    dialog?.addEventListener('close', () => trigger?.focus(), { signal });
    if (dialog?.querySelector('[role="status"],[role="alert"]')) dialog.showModal();
  }
  disconnectedCallback() { this.abort?.abort(); }
}
for (const [name, component] of Object.entries({ 'nv-carousel': NVCarousel, 'nv-video': NVVideo, 'nv-background-video': NVBackgroundVideo, 'nv-tabs': NVTabs, 'nv-sticky-cart': NVStickyCart, 'nv-support': NVSupport })) {
  if (!customElements.get(name)) customElements.define(name, component);
}
function nvInitialize(root = document) {
  root.querySelectorAll('[data-nv-accordion="true"]').forEach(group => {
    if (group.dataset.ready) return; group.dataset.ready = 'true';
    group.querySelectorAll('details').forEach(details => details.addEventListener('toggle', () => {
      if (details.open) group.querySelectorAll('details').forEach(other => { if (other !== details) other.open = false; });
    }));
  });
  root.querySelectorAll('.header__inline-menu header-menu').forEach(menu => {
    if (menu.dataset.nvReady) return; menu.dataset.nvReady = 'true';
    const details = menu.querySelector('details'), summary = menu.querySelector('summary');
    if (!details || !summary) return;
    menu.addEventListener('pointerenter', e => { if (e.pointerType === 'mouse' && innerWidth >= 990) { clearTimeout(menu.nvTimer); clearTimeout(menu.nvOpenTimer); menu.nvOpenTimer = setTimeout(() => { details.open = true; summary.setAttribute('aria-expanded', 'true'); }, 150); } });
    summary.addEventListener('click', () => clearTimeout(menu.nvOpenTimer));
    menu.addEventListener('pointerleave', () => { clearTimeout(menu.nvOpenTimer); menu.nvTimer = setTimeout(() => { if (!menu.contains(document.activeElement)) { details.open = false; summary.setAttribute('aria-expanded', 'false'); } }, 120); });
  });
}
nvInitialize(); document.addEventListener('shopify:section:load', e => nvInitialize(e.target));

class NVNewsletterPopup extends HTMLElement {
  connectedCallback() {
    this.abort?.abort(); this.abort = new AbortController(); const { signal } = this.abort;
    this.dialog = this.querySelector('dialog'); if (!this.dialog) return;
    this.key = `nv-newsletter-${this.dataset.key}`;
    let until = 0; try { until = Number(localStorage.getItem(this.key)); } catch (_) {}
    const open = () => { if (!this.dialog.open && !document.querySelector('dialog[open],cart-drawer.active,header-drawer details[open],details-modal details[open]') && !document.hidden) this.dialog.showModal(); };
    this.querySelector('[data-nv-close]')?.addEventListener('click', () => this.dialog.close(), { signal });
    this.dialog.addEventListener('close', () => { try { localStorage.setItem(this.key, String(Date.now() + Number(this.dataset.days) * 86400000)); } catch (_) {} }, { signal });
    if (this.querySelector('[role="alert"],[role="status"]')) open();
    else if (this.dataset.editor !== 'true' && Date.now() > until) this.timer = setTimeout(open, Number(this.dataset.delay) || 15000);
    document.addEventListener('shopify:section:select', e => { if (e.target.contains(this)) open(); }, { signal });
  }
  disconnectedCallback() { clearTimeout(this.timer); this.abort?.abort(); this.dialog?.close(); }
}
if (!customElements.get('nv-newsletter-popup')) customElements.define('nv-newsletter-popup', NVNewsletterPopup);
